import { regionCode } from '../../regions.js';

/** FDA M1 2.6 III.B.2.a: activity ID is the first sequence of that activity, not a control/receipt number. */
export function fdaActivityStartSequenceError(
  start: string | null | undefined,
  currentSequence: string | null | undefined,
  options: { required?: boolean } = {},
): string | null {
  if (start == null || start === '') {
    return options.required ? 'Enter the four-digit sequence that started this FDA regulatory activity.' : null;
  }
  if (!/^[0-9]{4}$/.test(start)) return 'FDA activity start sequence must contain exactly four digits, including leading zeros.';
  if (!currentSequence || !/^[0-9]{4}$/.test(currentSequence)) return 'Set a four-digit current sequence before confirming the FDA activity start sequence.';
  if (Number(start) > Number(currentSequence)) return 'FDA activity start sequence cannot be later than the current sequence.';
  return null;
}

export interface FdaActivitySequenceRow {
  Id: string;
  SequenceNumber: string;
  Region?: string;
  FdaActivityStartSequence?: string | null;
}

/** Rows must be the tenant/application family read under the reorder transaction's lock. */
export function fdaActivityReorderConflicts(
  rows: readonly FdaActivitySequenceRow[],
  effectiveSequences: ReadonlyMap<string, number>,
): string[] {
  // Review F6: an accepted spelling ('fda', ' FDA ') is FDA too. The raw
  // `=== 'FDA'` comparison let such rows skip the activity-start protection.
  const isFda = (row: FdaActivitySequenceRow): boolean => regionCode(row.Region) === 'FDA';
  const movingStartNumbers = new Set(rows.filter((row) => isFda(row) &&
    effectiveSequences.has(row.Id.toUpperCase()) &&
    effectiveSequences.get(row.Id.toUpperCase()) !== Number(row.SequenceNumber)).map((row) => row.SequenceNumber));
  return rows.filter((row) => {
    if (!isFda(row) || !row.FdaActivityStartSequence) return false;
    const next = effectiveSequences.get(row.Id.toUpperCase()) ?? Number(row.SequenceNumber);
    return movingStartNumbers.has(row.FdaActivityStartSequence) || Number(row.FdaActivityStartSequence) > next;
  }).map((row) => row.Id);
}

export class FdaActivityReorderConflictError extends Error {
  constructor(readonly submissionIds: string[]) {
    super('Reordering would change an FDA regulatory activity’s starting sequence or place a submission before it. Keep the activity start at its recorded sequence; review the affected activity assignments before reordering. Nothing was changed.');
  }
}
